import type { PostingExprNode, PostingNode } from '@revelio/data-access';

import type { CustomSkillTaxonomySelection } from '../../../filters/definitions/skillTaxonomy';
import { findFilterById } from '../../../filters/engine/filterRegistry';
import {
  type Filter,
  FilterId,
  FilterType,
  isMultiFilter,
  type SingleValueFilter,
  type TreeFilter,
} from '../../../filters/types/filters';
import { getSkillTierForLevelId } from '../../../filters/utils/role-skill-ent-kind';
import {
  buildSkillValNode,
  buildTreeFilterNodes,
  type RefData,
  wrapExprNode,
} from './node-builders';

export const getSkillTaxonomyId = (filters: Filter[]): number => {
  const skillTaxonomyFilter = findFilterById(filters, FilterId.SKILL_TAXONOMY);
  if (!skillTaxonomyFilter || skillTaxonomyFilter.type !== FilterType.VALUE) {
    return 0;
  }

  const value = (
    skillTaxonomyFilter as SingleValueFilter<CustomSkillTaxonomySelection>
  ).value;
  if (value && typeof value.taxonomyId === 'number') {
    return value.taxonomyId;
  }
  return 0;
};

const makeSkillLeafFactory =
  (taxonomyId: number) =>
  (id: number, levelId: string): PostingExprNode | null => {
    const level = getSkillTierForLevelId(levelId);
    if (level === null) return null;
    const ref: RefData = { level, taxonomyId };
    return buildSkillValNode(id, ref);
  };

export const buildCustomSkillFilter = (
  filters: Filter[]
): PostingNode | null => {
  const customSkillFilter = findFilterById(filters, FilterId.CUSTOM_SKILL);
  if (!customSkillFilter || customSkillFilter.type !== FilterType.TREE) {
    return null;
  }

  const makeLeaf = makeSkillLeafFactory(getSkillTaxonomyId(filters));
  const exprNode = buildTreeFilterNodes(customSkillFilter, makeLeaf);
  if (!exprNode) return null;

  return wrapExprNode(exprNode, customSkillFilter.isNot);
};

export const buildMultiCustomSkillFilter = (
  filters: Filter[]
): PostingNode | PostingNode[] | null => {
  const multiCustomSkill = findFilterById(filters, FilterId.MULTI_CUSTOM_SKILL);
  if (!multiCustomSkill || !isMultiFilter(multiCustomSkill)) {
    return null;
  }
  if (multiCustomSkill.instances.length === 0) {
    return null;
  }

  const makeLeaf = makeSkillLeafFactory(getSkillTaxonomyId(filters));

  const instanceNodes = multiCustomSkill.instances
    .filter((inst): inst is TreeFilter => inst?.type === FilterType.TREE)
    .map((instance) => {
      const exprNode = buildTreeFilterNodes(instance, makeLeaf);
      if (!exprNode) return null;
      return wrapExprNode(exprNode, instance.isNot);
    })
    .filter((node): node is PostingNode => node !== null);

  if (instanceNodes.length === 0) return null;
  if (instanceNodes.length === 1) {
    const [singleNode] = instanceNodes;
    return singleNode ?? null;
  }
  return instanceNodes;
};
